import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import type { CollectionScoringStatus, CollectionScoringSummary } from '../../generated';
import type { RequestCollectionScoringBody, Retailer } from '../../generated';
import { listCollectionScoringsV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdScoringsGet } from '../../generated';

/*
 * GET /v1/workspaces/{workspace_id}/assets/images/collections/{collection_id}/scorings: the product categories a
 * collection has been scored under against one benchmark, a custom one by `benchmark_id` or a Vizit one by
 * `retailer`, most recently selected first. `toCollectionScoring` is the one place the response is mapped. Tests
 * answer this path with `tests/mocks/collection-scoring-handlers.ts`.
 */

/**
 * One scoring of a collection under a product category. The listing answers the generated `CollectionScoringSummary`;
 * the request route answers `CollectionScoringView`, which has no category name, so the name is optional here.
 */
export type CollectionScoringResponse = Omit<CollectionScoringSummary, 'product_category_name'> & {
  product_category_name?: string | null;
};

/** A scoring of a collection under a product category against one benchmark, mapped for the UI. */
export interface CollectionScoring {
  id: string;
  /** Set for custom and Vizit pairs alike; Vizit pairs also carry their `retailer`. */
  benchmarkId: string;
  /** `null` for custom benchmarks; a Vizit benchmark is identified by its retailer. */
  retailer: Retailer | null;
  productCategoryId: string;
  /** `null` when the route did not include it; callers can resolve it from the product categories. */
  productCategoryName: string | null;
  /** Pending while the collection's images are being scored; a scoring pending for 30 minutes turns failed. */
  status: CollectionScoringStatus;
  requestedAt: string;
  readyAt: string | null;
  /** When the user last picked this category; the most recent one is the page's initial selection. */
  lastSelectedAt: string | null;
}

export function toCollectionScoring(response: CollectionScoringResponse): CollectionScoring {
  return {
    id: response.id,
    benchmarkId: response.benchmark_id,
    retailer: response.retailer,
    productCategoryId: response.product_category_id,
    productCategoryName: response.product_category_name ?? null,
    status: response.status,
    requestedAt: response.requested_at,
    readyAt: response.ready_at,
    lastSelectedAt: response.last_selected_at,
  };
}

/** The benchmark to list for: a custom one by `benchmark_id` or a Vizit one by `retailer`, never both. */
export type CollectionBenchmarkRef = Pick<RequestCollectionScoringBody, 'benchmark_id' | 'retailer'>;

export type ListCollectionScoringsQueryVariables = {
  workspace_id: string;
  collection_id: string;
} & CollectionBenchmarkRef;

export type ListCollectionScoringsQuery = CollectionScoring[];

async function listCollectionScorings(variables: ListCollectionScoringsQueryVariables) {
  const { workspace_id, collection_id, benchmark_id, retailer } = variables;
  const { data } = await listCollectionScoringsV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdScoringsGet({
    path: { workspace_id, collection_id },
    query: { benchmark_id: benchmark_id ?? undefined, retailer: retailer ?? undefined },
    throwOnError: true,
  });
  return (data ?? []).map(toCollectionScoring);
}

export const useListCollectionScoringsQuery = <TData = ListCollectionScoringsQuery, TError = unknown>(
  variables: ListCollectionScoringsQueryVariables,
  options?: Omit<UseQueryOptions<ListCollectionScoringsQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<ListCollectionScoringsQuery, TError, TData>({
    queryKey: useListCollectionScoringsQuery.getKey(variables),
    queryFn: () => listCollectionScorings(variables),
    ...options,
  });
};

useListCollectionScoringsQuery.getKey = (variables: ListCollectionScoringsQueryVariables) => {
  const { workspace_id, collection_id, benchmark_id = null, retailer = null } = variables;
  return ['ListCollectionScorings', { workspace_id, collection_id, benchmark_id, retailer }];
};

useListCollectionScoringsQuery.fetcher = (variables: ListCollectionScoringsQueryVariables) => () =>
  listCollectionScorings(variables);
